/**
 * calcSignature.js — Stale-calculation guard.
 *
 * A quote's price is a pure function of a known set of wizard inputs. We take a
 * labelled snapshot of those inputs at the moment of a successful /calculate and
 * compare it against the live form on every render. Any drift means the numbers
 * on screen (and the calc blob the save endpoints trust) are stale, so the
 * operator must recalculate before saving — otherwise the canonical validator
 * rejects the save and the quote is lost.
 *
 * Display-only settings (show_breakup, pdf_theme, terms text, guest details …)
 * are deliberately NOT tracked: they never move a number.
 */

const stableStringify = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((k) => `${k}:${stableStringify(value[k])}`)
      .join(',')}}`;
  }
  return String(value);
};

const num = (value) => Number(value || 0);

const scalar = (label, value) => ({
  label,
  kind: 'scalar',
  value: value === null || value === undefined ? '' : String(value),
});

const blob = (label, value) => ({ label, kind: 'blob', value: stableStringify(value) });

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatCalcValue = (value) => {
  if (value === '' || value === null || value === undefined) return '—';
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (iso) return `${Number(iso[3])} ${MONTHS[Number(iso[2]) - 1]} ${iso[1]}`;
  return String(value);
};

const markupLabel = (f) =>
  (f.markup_type === 'amount'
    ? `₹${num(f.markup_amount)}${f.markup_per_person ? ' per person' : ''}`
    : `${num(f.markup_pct)}%`);

const discountLabel = (f) =>
  (f.discount_type === 'amount'
    ? `₹${num(f.discount_amount)}${f.discount_per_person ? ' per person' : ''}`
    : `${num(f.discount_pct)}%`);

const roomsLabel = (f) => {
  const rc = f.room_config || {};
  const single = rc.single_rooms ?? f.single_rooms ?? f.no_single_rooms ?? 0;
  const double = rc.double_rooms ?? f.double_rooms ?? f.no_double_rooms ?? 0;
  const triple = rc.triple_rooms ?? f.triple_rooms ?? f.no_triple_rooms ?? 0;
  const extra = rc.adult_extra_beds ?? f.extra_beds ?? 0;
  return `${num(single)} single / ${num(double)} double / ${num(triple)} triple / ${num(extra)} extra bed`;
};

const miscShape = (list) =>
  (list || []).map((m) => ({
    label: m.remarks || m.label || '',
    amount: num(m.amount),
    cost_type: m.cost_type || '',
    gst: num(m.gst_percentage),
    optional: !!m.is_optional,
  }));

const p2pShape = (list) =>
  (list || []).map((p) => ({
    from: p.from_place || '',
    to: p.to_place || '',
    mode: p.transport_mode || '',
    type: p.price_type || p.pricing_type || '',
    qty: num(p.quantity),
    rate: num(p.rate ?? p.cost),
    gst: num(p.gst_slab ?? p.gst_pct),
  }));

const stopsShape = (list) =>
  (list || []).map((s) => ({
    place: s.place_name || s.place_id || '',
    nights: num(s.nights),
    order: num(s.order ?? s.order_seq),
  }));

const packageHotelsShape = (selections) =>
  Object.keys(selections || {})
    .sort()
    .map((key) => {
      const sel = selections[key] || {};
      const hotels = Array.isArray(sel.hotels) ? sel.hotels : [sel];
      return {
        stop: key,
        check_in: sel.check_in || '',
        check_out: sel.check_out || '',
        hotels: hotels.map((h) => ({
          id: h.hotel_id || '',
          name: h.hotel_name || '',
          room: h.room_name || h.room_type || '',
          meal_plan: h.meal_plan || '',
          nights: num(h.nights),
          rate: num(h.lowest_rate),
        })),
      };
    });

const manualShape = (mpd) => ({
  route: stopsShape(mpd?.route),
  total_nights: num(mpd?.total_nights),
  total_days: num(mpd?.total_days),
  options: (mpd?.options || []).map((o) => ({
    name: o.option_name || '',
    accommodation: (o.accommodation || []).map((a) => ({
      place: a.place_name || '',
      hotel: a.hotel_name || '',
      room: a.room_name || a.room_type || '',
      rate: num(a.room_rate_per_night),
      rooms: num(a.num_rooms),
      nights: num(a.nights),
      meal_plan: a.meal_plan || '',
      gst: num(a.gst_pct),
    })),
    transfer: {
      rate: num(o.package_transfer?.rate_per_vehicle),
      vehicles: num(o.package_transfer?.num_vehicles),
      gst: num(o.package_transfer?.gst_pct),
    },
  })),
  p2p: p2pShape(mpd?.p2p_transfers),
});

const dmcShape = (content) => {
  const parsed = content?.parsed || {};
  return {
    total_nights: num(parsed.total_nights),
    total_days: num(parsed.total_days),
    pricing: (parsed.pricing_options || []).map((p) => ({
      name: p.option_name || '',
      per_person: num(p.per_person),
      child_with_bed: num(p.child_with_bed),
      child_no_bed: num(p.child_no_bed),
      extra_adult: num(p.extra_adult),
    })),
    accommodation: (parsed.accommodation_options || []).map((o) => ({
      name: o.option_name || '',
      hotels: (o.hotels || []).map((h) => ({
        place: h.place || '',
        hotel: h.hotel || '',
        nights: num(h.nights),
        meal_plan: h.meal_plan || '',
      })),
    })),
  };
};

/**
 * Labelled snapshot of every price-affecting input.
 * @param {object} formData wizard form state
 * @param {object} extras   { customPackageHotelSelections, selectedVehicles }
 */
export const buildCalcSnapshot = (formData = {}, extras = {}) => {
  const f = formData;
  const snapshot = {
    quotation_type: scalar('Quotation type', f.route_type),
    adults: scalar('Adults', num(f.no_adults)),
    children_with_bed: scalar('Children with bed', num(f.no_children_with_bed)),
    children_no_bed: scalar('Children without bed', num(f.no_children_no_bed)),
    infants: scalar('Infants', num(f.no_infants)),
    arrival_date: scalar('Arrival date', f.arrival_date),
    arrival_city: scalar('Arrival city', f.arrival_city),
    departure_city: scalar('Departure city', f.departure_city),
    categories: scalar('Hotel categories', [...(f.selected_categories || [])].sort().join(', ')),
    meal_plans: scalar('Meal plans', [...(f.meal_plans || [])].sort().join(', ')),
    classification_mode: scalar('Category mode', f.classification_mode),
    vehicle: scalar(
      'Vehicle',
      f.vehicle_type_selected || f.base_vehicle_type ||
        (f.route_type === 'custom_package' ? '' : [...(extras.selectedVehicles || [])].sort().join(', '))
    ),
    vehicle_counts: blob('Vehicle count', f.vehicle_counts || {}),
    quotation_format: scalar('Pricing format', f.quotation_format),
    transfer_days: scalar('Transfer days', f.transfer_days ?? ''),
    rooms: scalar('Room configuration', roomsLabel(f)),
    markup: scalar('Markup', markupLabel(f)),
    discount: scalar('Discount', discountLabel(f)),
    gst: scalar(
      'GST',
      `${num(f.gst_percentage)}%${f.gst_included ? ' (included)' : ''} on ${f.gst_apply_on || 'total'}`
    ),
    misc_costs: blob('Miscellaneous costs', miscShape(f.misc_costs)),
    p2p_transfers: blob('Point-to-point transfers', p2pShape(f.p2p_transfers)),
    hotel_overrides: blob('Hotel selection', f.hotel_overrides || {}),
  };

  if (f.route_type === 'predefined') {
    snapshot.route = scalar('Route', f.route_id);
  } else if (f.route_type === 'custom') {
    snapshot.custom_route = blob('Route stops', stopsShape(f.custom_route));
  } else if (f.route_type === 'custom_package') {
    snapshot.package_stops = blob('Package stops', stopsShape(f.custom_package_data?.stops));
    snapshot.package_vehicle = scalar('Package vehicle', f.custom_package_data?.vehicle_type);
    snapshot.package_transfer_days = scalar(
      'Package transfer days',
      f.custom_package_data?.transfer_days ?? ''
    );
    // Watch EVERY option's hotels / rooms / meal plans, not just the first —
    // each option is its own price tier.
    const pkgOptions = f.custom_package_options || [];
    snapshot.package_hotels = blob(
      'Hotel selection',
      pkgOptions.length > 0
        ? pkgOptions.map((o) => ({
            name: o.option_name || '',
            hotels: packageHotelsShape(o.selected_hotels),
          }))
        : packageHotelsShape(extras.customPackageHotelSelections)
    );
  } else if (f.route_type === 'manual_package') {
    snapshot.manual_package = blob('Manual package details', manualShape(f.manual_package_data));
  } else if (f.route_type === 'dmc_package') {
    snapshot.dmc_package = blob('DMC package details', dmcShape(f.dmc_content));
  }

  return snapshot;
};

/**
 * Human-readable list of what changed since the snapshot was taken.
 * @returns {Array<{key:string,label:string,from?:string,to?:string,text:string}>}
 */
export const diffCalcSnapshots = (before, after) => {
  if (!before || !after) return [];
  const changes = [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  keys.forEach((key) => {
    const a = before[key];
    const b = after[key];
    const av = a ? a.value : '';
    const bv = b ? b.value : '';
    if (av === bv) return;
    const label = (b && b.label) || (a && a.label) || key;
    const kind = (b && b.kind) || (a && a.kind) || 'scalar';
    if (kind === 'blob') {
      changes.push({ key, label, text: `${label} changed` });
    } else {
      changes.push({
        key,
        label,
        from: av,
        to: bv,
        text: `${label}: ${formatCalcValue(av)} → ${formatCalcValue(bv)}`,
      });
    }
  });
  return changes;
};

export default buildCalcSnapshot;
